// Draft assist for human riders: picks the lane with the most shelter so a rider on a trainer, hands on the bars,
// does not have to steer onto every wheel. It only ever places the rider: it never denies a wheel to anyone,
// never swerves away from a hazard (dodging stays a skill) and always yields to manual steering.
import { combineShelter, wakeShelter, resistance, clearance, BIKE_LEN } from './physics.js';
import { mod, LANE_W, ROAD_EDGE } from './track.js';
import { aimOf } from './bots.js';

/**
 * Tuning. `range`: horizon for a wheel ahead (m). `every`: seconds between two looks, a place is taken only when
 * it wins two looks in a row (reaction of 0.3 to 0.6 s, like a bot). `gain`: shelter a new place must add (in proportion below
 * `small` units), plus `cost` per unit of road crossed: a move has to pay for itself. `hold`: seconds a rider stays on the wheel they took before
 * considering another. `reach`: longest move (units) while sheltered or with riders close ahead, only a rider in the open (below `open`, nobody within `near`) crosses the road.
 * `openGain`, `openCost`: the same for a rider in the open, who only prefers the nearest of equal wheels. `urgent`: closing speed (m/s) above which a pass takes the full human angle (so does a pass the rider pushes for, or starts overlapping the wheel).
 * `follow`: how far (units) a wheel may move away and still be followed rather than given up.
 * `manual`: seconds the assist stays quiet after the rider steers. `near`/`slower`: a rider this close and this
 * much slower is about to be passed, not a wheel to move in behind. `passGap`/`closing`: on a wheel, closing in
 * this fast from this close, or overlapping closer than `overlap` (m, axle to axle: over half the shelter gone),
 * the assist moves out to pass instead of riding through. `passMax`: give up a pass (s). `passSide`: the sideways moves
 * that clear a wheel, nearest first (units).
 * `hazard`: horizon (m) within which a place with a hazard is never chosen. `settled`: a place this close (about 35 cm) is not worth a move.
 */
export const ASSIST = {
  range: 20,
  every: 0.3,
  gain: 0.04,
  cost: 0.015,
  openGain: 0.005,
  openCost: 0.003,
  urgent: 1,
  small: 0.5,
  hold: 8,
  reach: 1.5,
  open: 0.05,
  follow: 0.6,
  manual: 5,
  near: 8,
  slower: 0.5,
  passGap: 3,
  closing: 0.4,
  overlap: 1.2,
  passMax: 8,
  passSide: [0.5, 0.75, 1],
  hazard: 30,
  settled: 0.15,
};

/**
 * The wheel hold. A human on a wheel (within `side` road units of its line, `range` m behind it at most) is held a wheel's
 * `gap` behind it: a bot holds a wheel by reading its watts to the unit, a rider on a trainer cannot, so the engine feathers
 * their brakes (physics FEATHER at most) to absorb the small surplus of speed that would run them into the wheel, as a
 * rider does with two fingers on the lever. A rider who pushes on, `surplus` m/s² of effort over what holds the wheel
 * (about seventy watts at bunch speed, smoothed over `smooth` s), is passing and is let go; a rider who steers out is let
 * go once beside the wheel, not while still on its line. `pull`: how fast the gap is closed (m/s per m off the wheel),
 * `close`: the most (m/s) the hold closes at.
 */
export const HOLD = { gap: 1.9, side: 0.3, range: 4, surplus: 0.1, smooth: 2, pull: 0.8, close: 1 };

/** The rider the hold reads: speed and the surplus of effort being absorbed (m/s², smoothed). */
export interface HeldRider {
  v: number;
  held: number;
}
/** What the rider's watts are up against this step: driving power (W), mass (kg), gradient (fraction) and sheltered CdA (m²). */
export interface HeldEffort {
  power: number;
  mass: number;
  grade: number;
  cda: number;
}
/**
 * The brake (m/s²) that holds rider `r` on wheel `w` (on their line, HOLD.side), `gap` m ahead, for this step. The surplus
 * of effort over what holds the wheel is recorded in `r.held`; 0 (no hold) when the rider is passing (surplus over
 * `HOLD.surplus`) or slower than the wheel.
 */
export function holdOnWheel(r: HeldRider, w: { v: number }, gap: number, e: HeldEffort, dt: number): number {
  const vHold = Math.max(0, w.v + Math.max(-2, Math.min(HOLD.close, (gap - HOLD.gap) * HOLD.pull)));
  // the watts over those that hold the wheel's pace, as an acceleration: what the rider is asking beyond sitting in
  const over = (e.power - resistance(vHold, e.grade, e.mass, e.cda) * Math.max(vHold, 1.5)) / (e.mass * Math.max(r.v, 1.5));
  r.held += (over - r.held) * Math.min(1, dt / HOLD.smooth);
  if (r.held > HOLD.surplus || r.v <= vHold) return 0;
  return (r.v - vHold) / dt;
}

export interface AssistState {
  /** Seconds left before the next look. */
  t: number;
  /** Lane that won the last look (null: none), confirmed on the next one. */
  want: number | null;
  /** Seconds left of quiet after a manual steer. */
  quiet: number;
  /** Rider being passed: excluded from the wheels until passed, and seconds left before giving up. */
  pass: AssistRider | null;
  passT: number;
  /** Closing in too fast for a calm drift: this pass takes the rider's full angle. */
  urgent: boolean;
  /** Rider whose wheel is being followed, and seconds left before another may be considered. */
  wheel: AssistRider | null;
  holdT: number;
}

/** What the assist reads of a rider: a subset of the race's Rider. */
export interface AssistRider {
  s: number;
  v: number;
  lane: number;
  targetLane: number;
  out: boolean;
  finishT: number | null;
  /** Surplus of effort the wheel hold absorbs (m/s², see HOLD); over HOLD.surplus the rider wants past. */
  held?: number;
  /** A bot: pulling off at the end of a turn (let them drift back, never take their wheel), and where its line really heads (the wander and the bunch's bend on top of its chosen place). */
  ai?: { restT: number; sway: number; bend: number } | null;
}
export interface AssistHazard {
  s: number;
  lane: number;
}

export const newAssist = (): AssistState => ({ t: 0, want: null, quiet: 0, pass: null, passT: 0, urgent: false, wheel: null, holdT: 0 });

/** The rider steered by hand: the assist steps aside for a while and forgets any pending move. */
export function assistManual(a: AssistState) {
  a.quiet = ASSIST.manual;
  a.want = null;
  a.pass = null;
  a.wheel = null;
  a.holdT = 0;
}

/** The assist is placing the rider, in no hurry: they drift over like a bot rather than swerve. A hand on the bars or an urgent pass keeps the full angle. */
export const assistCalm = (a: AssistState | null): boolean => !!a && a.quiet <= 0 && !(a.pass && a.urgent);

/** Places the assist considers besides the wheels' own lines: a sample of the free road, not lanes. */
const ROAD = [-1.5, -1, -0.5, 0, 0.5, 1, 1.5];
/** Signed gap along the loop from `r` to `o` (positive: `o` is ahead). */
const gapOf = (r: AssistRider, o: AssistRider, L: number) => mod(o.s - r.s + L / 2, L) - L / 2;

/**
 * One assist tick for rider `r`. Returns the lane to steer to, or null to leave the rider where they are.
 * Deterministic: no randomness, only the race state.
 */
export function assistStep(
  a: AssistState,
  r: AssistRider,
  riders: readonly AssistRider[],
  hazards: readonly AssistHazard[],
  L: number,
  dt: number,
): number | null {
  if (a.quiet > 0) {
    a.quiet -= dt;
    return null;
  }
  if (a.pass) {
    a.passT -= dt;
    const p = a.pass,
      g = gapOf(r, p, L);
    // passed, dropped back behind (no longer closing), out of sight or taking too long: the pass is over
    if (p.out || g < -1 || (g > ASSIST.passGap && r.v < p.v + 0.1) || g > ASSIST.range || a.passT <= 0) a.pass = null;
  }
  if (a.holdT > 0) a.holdT -= dt;
  a.t -= dt;
  if (a.t > 0) return null;
  a.t = ASSIST.every;
  if (r.out || r.finishT != null) {
    a.want = null;
    return null;
  }

  const hazard = (lane: number) =>
    hazards.some(z => {
      const d = mod(z.s - r.s, L);
      return d > 0 && d < ASSIST.hazard && Math.abs(z.lane - lane) < 0.6;
    });
  // never move over onto a rider who would then be too close beside (the room two riders keep, physics clearance: all
  // of it level, less the more they overlap, as in a bunch), nor back in front of the one being passed; the rider
  // already ahead on our line is a wheel, not an obstacle: centring on them is allowed
  const occupied = (lane: number) =>
    riders.some(o => {
      if (o === r || o.out) return false;
      const g = gapOf(r, o, L),
        side = Math.abs(o.lane - lane) * LANE_W;
      if (o === a.pass) return g > -BIKE_LEN && g < ASSIST.passGap + 3 && side < clearance(0);
      if (g > 0.9 && Math.abs(o.lane - r.lane) < 0.5) return false;
      // level with us: no place within their room, nor across them
      return Math.abs(g) < BIKE_LEN && (side < clearance(g) || (o.lane - r.lane) * (o.lane - lane) < 0);
    });
  const blocked = (lane: number) => hazard(lane) || occupied(lane);
  const ahead: { o: AssistRider; gap: number }[] = [];
  for (const o of riders) {
    if (o === r || o.out || o === a.pass || (o.ai && o.ai.restT > 0)) continue;
    const gap = gapOf(r, o, L);
    if (gap > 0.9 && gap <= ASSIST.range) ahead.push({ o, gap });
  }
  const here = r.targetLane;

  // on a wheel and closing in fast, or overlapping it (most of the shelter gone): come round rather than ride into it
  const wheel = ahead.filter(w => Math.abs(w.o.lane - r.lane) < 0.5 && w.gap < ASSIST.passGap).sort((x, y) => x.gap - y.gap)[0];
  const lineOf = (o: AssistRider) => aimOf(o);
  const closing = wheel ? r.v - wheel.o.v : 0;
  // already moving out of one pass, only a real overlap starts another; a wheel pulling away clears by itself. The wheel
  // hold absorbs a small surplus of effort: a rider pushing past it wants round, and is moved out before they run into the wheel
  const pushing = (r.held ?? 0) > HOLD.surplus;
  if (wheel && (closing > ASSIST.closing || pushing || (wheel.gap < ASSIST.overlap && closing > -0.1)) && (!a.pass || wheel.gap < ASSIST.overlap)) {
    const free = ASSIST.passSide.flatMap(d => [here - d, here + d]).filter(l => Math.abs(l) <= ROAD_EDGE && !blocked(l));
    if (free.length) {
      // a pass decided on (the rider pushing past the hold, or already overlapping the wheel) is made decisively, at the full angle
      a.pass = wheel.o;
      a.passT = ASSIST.passMax;
      a.urgent = closing > ASSIST.urgent || pushing || wheel.gap < ASSIST.overlap;
      a.want = null;
      a.wheel = null;
      a.holdT = ASSIST.hold;
      const rest = ahead.filter(w => w.o !== wheel.o);
      return free.map(l => ({ l, v: shelterIn(l, rest) - ASSIST.cost * Math.abs(l - here) })).sort((x, y) => y.v - x.v)[0].l;
    }
  }

  // otherwise a wheel: never behind a rider about to be passed, never onto a hazard
  const wheels = ahead.filter(w => !(w.gap < ASSIST.near && Math.abs(lineOf(w.o) - here) > 0.5 && w.o.v < r.v - ASSIST.slower));
  const cur = shelterIn(here, wheels);
  // the wheel being followed: its small moves are followed, the rider stays on it for a while and never hunts for better
  const mine = a.wheel && wheels.find(w => w.o === a.wheel);
  if (mine && Math.abs(lineOf(mine.o) - here) <= ASSIST.follow) {
    if (a.holdT > 0) {
      const to = lineOf(mine.o);
      a.want = null;
      return Math.abs(to - here) >= ASSIST.settled && !blocked(to) ? to : null;
    }
  } else a.wheel = null;
  // just moved, sheltered or in the bunch: stay put rather than hunt
  if (a.holdT > 0 && (cur >= ASSIST.open || ahead.some(w => w.gap < ASSIST.near))) {
    a.want = null;
    return null;
  }
  // a rider sheltered looks close by, one in the open anywhere on the road; the road is sampled besides the wheels' own lines
  const sheltered = cur >= ASSIST.open,
    crowded = ahead.some(w => w.gap < ASSIST.near);
  const reach = sheltered || crowded ? ASSIST.reach : Infinity;
  const lines = [...ROAD];
  for (const w of wheels) {
    const l = lineOf(w.o);
    if (!lines.some(x => Math.abs(x - l) < 1e-6)) lines.push(l);
  }
  const gain = sheltered ? ASSIST.gain : ASSIST.openGain;
  let best = here,
    bestV = cur;
  for (const l of lines) {
    const d = Math.abs(l - here);
    if (d < ASSIST.settled || d > reach || blocked(l)) continue;
    // centring on a wheel a few centimetres away asks little, a real move has to pay for itself
    const v = shelterIn(l, wheels) - gain * Math.min(1, d / ASSIST.small) - (sheltered ? ASSIST.cost : ASSIST.openCost) * d;
    if (v > bestV) {
      best = l;
      bestV = v;
    }
  }
  if (best === here) {
    a.want = null;
    // no better place: keep following the wheel, in small steps
    const to = a.wheel ? lineOf(a.wheel) : null;
    return to != null && Math.abs(to - here) >= ASSIST.settled && !blocked(to) ? to : null;
  }
  if (a.want === null || Math.abs(a.want - best) > ASSIST.settled) {
    a.want = best;
    return null;
  }
  a.want = null;
  a.wheel = wheelOn(best, wheels);
  a.holdT = ASSIST.hold;
  return best;
}

/** The rider giving the most shelter on a line. */
function wheelOn(lane: number, wheels: readonly { o: AssistRider; gap: number }[]): AssistRider | null {
  let out: AssistRider | null = null,
    top = 0;
  for (const w of wheels) {
    const sh = wakeShelter(w.gap, (aimOf(w.o) - lane) * LANE_W);
    if (sh > top) {
      top = sh;
      out = w.o;
    }
  }
  return out;
}

/** Shelter a rider would get in `lane` from the riders ahead, once everyone is on the line they are heading to. */
function shelterIn(lane: number, ahead: readonly { o: AssistRider; gap: number }[]): number {
  return combineShelter(ahead.map(w => wakeShelter(w.gap, (aimOf(w.o) - lane) * LANE_W)));
}
